import React, { useEffect, useState, useRef } from 'react'
import {
    Media,
    IconButton,
    Tile,
    DraggableTile,
    Input,
    Icon,
    Checkbox,
} from 'components'
import {
    ThumbSize,
    MediaType,
    BuilderData,
} from 'app/types'
import { getMediaUrl } from 'helpers/config'
import * as fnc from 'helpers/fnc'
import { Toggle } from './Core/Toggle'
import { usePreviewMedia } from 'app/hooks'

interface MediaTileProps {
    app: AppData,
    builder: BuilderData
    organization: OrganizationData,
    media: MediaData,
    onClick: () => void,
    selected: boolean,
    scrollTo: boolean
    isDialog: boolean,
    listRef: React.RefObject,
    draggable: boolean,
    onReorder: () => void,
    order: number,
    name: string,
    showInfo: boolean,
    onEditName: () => void,
    selectIcon: boolean,
    aspect: boolean,
    square: boolean,
    toggleValue: float | boolean,
    onToggle: () => void,
    toggleLabel: string,
    debug: boolean,
    onDownload?: () => void,
    downloadIcon?: string,
    hideMediaIcon?: boolean,
    tags?: MediaTagData[],
    showTagBadges?: boolean,
    fileTableLayout?: boolean,
    brokerPortalMode?: boolean,
}

export function MediaTile(props: MediaTileProps) {
    const {
        app,
        builder,
        organization,
        media,
        selected,
        onClick,
        scrollTo,
        isDialog,
        listRef,
        draggable,
        onReorder,
        order,
        hoverShow,
        name,
        onEditName,
        showInfo,
        children,
        selectIcon,
        aspect,
        square,
        toggleLabel,
        toggleValue,
        onToggle,
        debug,
        onDownload,
        downloadIcon,
        tags,
        showTagBadges,
        brokerPortalMode,
        ...mediaProps
    } = { isDialog: true, showInfo: true, selectIcon: true, showTagBadges: true, ...props }

    const [loaded, setLoaded] = useState(false)
    const [dragging, setDragging] = useState(false)
    const [editingName, setEditingName] = useState(false)
    const tileRef = useRef()
    const asList = media?.mediaTypeId == MediaType.File && (props.fileTableLayout !== false)
    const previewMedia = usePreviewMedia()

    useEffect(() => {
        if (debug) {
            console.log('[MediaTile] Props received:', {
                mediaId: media?.id,
                mediaName: media?.name,
                mediaTypeId: media?.mediaTypeId,
                appProvided: !!app,
                builderProvided: !!builder,
                orgProvided: !!organization,
                isDialog,
                selected,
                asList,
                onClick: !!onClick,
                onDownload: !!onDownload,
                downloadIcon
            })
        }
    }, [media, app, builder, organization, isDialog, debug, downloadIcon])

    // Not for this app
    /*if (app && (!media.appId || media.appId != app.meta.id)
        || (!app && (media.appId != null && media.appId != 'homegyde'))) {
        return null
    }*/

    if (!media) {
        console.warn('[MediaTile] Media is null or undefined')
        return null
    }

    useEffect(() => {
        if (media.mediaTypeId != MediaType.Image) {
            setLoaded(true)
        }
    }, [])

    useEffect(() => {
        if (!media) {
            return
        }
        if (scrollTo && tileRef.current && listRef.current) {
            const offsetTop = tileRef.current.offsetTop
            listRef.current.scrollTo({ y: `${offsetTop - 20}px` })

        }
    }, [tileRef && tileRef.current, listRef && listRef.current])

    function handleEditName(x) {
        onEditName(x)
        setTimeout(() => {
            setEditingName(false)
        }, 10)
    }

    function getName() {
        if (name || onEditName) {
            if (editingName) {
                return <div className="media-name">
                    <Input value={name} autoFocus onSubmit={handleEditName} onBlur={() => setEditingName(false)} />
                </div>
            }
            return <div className="media-name" style={onEditName ? { cursor: 'pointer' } : null} onClick={(e) => e.stopPropagation()}>
                {onToggle && getToggle()}
                <span onMouseDown={(e) => e.stopPropagation()} onMouseUp={onEditName ? (e) => { e.stopPropagation(); setEditingName(true) } : null}> {name ? name : 'Add Name'}</span>
                {onEditName && <IconButton noBg icon="fas fa-pen" onMouseDown={(e) => e.stopPropagation()} onMouseUp={(e) => { e.stopPropagation(); setEditingName(true) }} />}
            </div >
        }
        return null
    }

    function handleToggle(x) {
        if (onToggle) {
            onToggle()
        }
    }

    function getToggle() {
        if (onToggle) {
            return <div className="media-toggle top-left">
                <Checkbox value={toggleValue} onChange={(val) => { handleToggle(val); }} />
            </div>
        }
    }

    function getInfo() {
        return <div className="media-info noselect">
            <span>{media.name}</span>
        </div>
    }

    function getTagBadges() {
        if (!showTagBadges || !media.mediaTagId || !tags || tags.length === 0) {
            return null
        }

        const tag = tags.find(t => t.id === media.mediaTagId)
        if (!tag) {
            return null
        }

        return <div className="media-tag-badges">
            <span className="media-tag-badge">{tag.name}</span>
        </div>
    }
    if(!media) {
        console.warn('[MediaTile] Attempting to render with null/undefined media')
        return <Tile className={className} style={{ opacity: 1 }} forwardRef={tileRef} square={!aspect} aspect={aspect} onClick={tileOnClick}>
            <h3>Missing Media</h3>
        </Tile>
    }

    if (!app && debug) {
        console.warn('[MediaTile] Warning: app prop is null or undefined. Media:', media?.id, media?.name)
    }

    let mediaElem = null
    let tileOnClick = null
    const finalMedia = media//previewMedia(app, media)
    const tourPreviewMedia = media.mediaTypeId == MediaType.Tour ? previewMedia(app || organization, media) : null
    switch (media.mediaTypeId) {
        case MediaType.EmailResource:
        case MediaType.Image:
        case MediaType.Video:
        case MediaType.Tour:
            if (media.mediaTypeId == MediaType.Tour && !tourPreviewMedia) {
                mediaElem = <div className="media-file-icon noselect media-tour-placeholder">
                    <i className="fas fa-walking" style={{ fontSize: '48px', color: '#666' }} />
                    <span className="media-file-name">{media.name || media.link || 'Virtual tour'}</span>
                </div>
                tileOnClick = (e) => onClick ? onClick(media, e) : null
            } else if (draggable) {
                mediaElem = <Media fadeIn key={`${media.id}${media.dateModified}`} app={app} builder={builder} organization={organization} url={finalMedia.url} mediaId={finalMedia.id} thumb={true} thumbSize={ThumbSize.Medium} fallback onLoad={() => setLoaded(true)} hoverShow={hoverShow && !dragging} hideMediaIcon={props.downloadIcon ? true : false} {...mediaProps} />
            } else {
                mediaElem = <Media fadeIn key={`${media.id}${media.dateModified}`} app={app} builder={builder} organization={organization} url={finalMedia.url} mediaId={finalMedia.id} thumb={true} thumbSize={ThumbSize.Medium} fallback onClick={(e) => onClick ? onClick(media, e) : null} onLoad={() => setLoaded(true)} hoverShow={hoverShow} hideMediaIcon={props.downloadIcon ? true : false} {...mediaProps} />
            }
            break
        case MediaType.Spin:
            const paddedIndex = String(0).padStart(3, '0')
            const quality = 'low'
            const path = `${getMediaUrl(app)}/`
            const url = `${path}${media.link}/${quality}/${media.link}_${quality}_${paddedIndex}.jpg`
            mediaElem = <Media fadeIn url={url} fallback onClick={(e) => onClick(media, e)} onLoad={() => setLoaded(true)} thumb={true} thumbSize={ThumbSize.Medium} hoverShow={hoverShow} />
            break
        case MediaType.File: {
            const isPdf = media.link?.toLowerCase().endsWith('.pdf')
            const fileIcon = isPdf ? 'fas fa-file-pdf' : 'fas fa-file'
            const iconColor = isPdf ? '#d32f2f' : '#666'
            mediaElem = <div className="media-file-icon noselect">
                <i className={fileIcon} style={{ fontSize: '48px', color: iconColor }} />
                <span className="media-file-name">{media.link}</span>
            </div>
            tileOnClick = (e) => onClick(media, e)
            break
        }
        default:
            mediaElem = <h3 className="noselect">{media.link}</h3>
            tileOnClick = (e) => onClick(media, e)
            break
    }

    if (draggable) {
        return <DraggableTile className={`media-tile${selected ? ' selected' : ''}`} style={{ opacity: loaded ? 1 : 0 }} forwardRef={tileRef} onReorder={onReorder} onClick={(e) => onClick ? onClick(media, e) : null} onDrag={setDragging} order={order} square>
            {mediaElem}
            {getInfo()}
            {getTagBadges()}
            {getName()}
            {getToggle()}
        </DraggableTile>
    }
    let className = `media-tile${selected ? ' selected' : ''}${asList ? ' as-list' : ''}`

    if (asList) {
        const tag = tags?.find(t => t.id === media.mediaTagId)
        return <tr className={className} onClick={tileOnClick}>
            <td>{media.name}</td>
            <td>
                <span>{fnc.dateFriendly(new Date(media.dateModified), { days: true, time: true })} </span>
            </td>
            <td>
                <span>{fnc.dateFriendly(new Date(media.dateCreated), { days: true, time: true })} </span>
            </td>
            <td>
                <span>{tag?.name || '-'}</span>
            </td>
            {selected && selectIcon && isDialog && <td>
                <IconButton noBg icon="fas fa-check" onClick={(e) => { e.stopPropagation(); onClick(media, e, true) }} />
            </td>}
        </tr>
    }


    return <Tile className={className} style={{ opacity: loaded ? 1 : 0, ...(brokerPortalMode && { position: 'relative', cursor: 'default' }) }} forwardRef={tileRef} square={!aspect} aspect={aspect} onClick={brokerPortalMode ? null : tileOnClick}>
        {brokerPortalMode && onClick && <IconButton 
            icon="fas fa-external-link-alt" 
            onClick={(e) => { 
                e.stopPropagation()
                onClick(media, e) 
            }}
            className="media-tile-download"
            large
            tertiary
            title="Open in new tab"
        />}
        {brokerPortalMode && onDownload && <IconButton 
            icon={props.downloadIcon || "fas fa-download"} 
            onClick={(e) => { 
                e.stopPropagation()
                onDownload()
            }}
            className="media-tile-download"
            large
            tertiary
            title="Download"
        />}
        {selected && selectIcon && isDialog && <IconButton icon="fas fa-check" onClick={(e) => { e.stopPropagation(); onClick(media, e, true) }} />}
        {!brokerPortalMode && onDownload && <IconButton icon={props.downloadIcon || "fas fa-download"} onClick={(e) => { e.stopPropagation(); onDownload() }} className="media-tile-download" large tertiary />}
        {getToggle()}
        {mediaElem}
        {getInfo()}
        {getTagBadges()}
        {getName()}
        {props.children}
    </Tile>
}
